import { useCallback, useState } from "react";
import { TfaMethod } from "@/core/constants/tfaMethod";
import AccountMpProvider from "@/core/data-provider/data-mp/account/account-mp-provider";
import useErrorTranslation from "@/core/helpers/useErrorTranslation";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";

export default function useChangePassword() {
  const tError = useErrorTranslation();
  const [sendIsPending, setSendIsPending] = useState(false);
  const [sendError, setSendError] = useState<string>("");
  const [sendIsCompleted, setSendIsCompleted] = useState<null | boolean>(null);

  const [checkIsPending, setCheckIsPending] = useState(false);
  const [checkError, setCheckError] = useState<string>("");
  const [checkIsCompleted, setCheckIsCompleted] = useState<null | boolean>(null);

  const [newPasswordIsPending, setNewPasswordIsPending] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState<string>("");
  const [newPasswordIsCompleted, setNewPasswordIsCompleted] = useState<null | boolean>(null);

  const sendCodeHandler = useCallback(async (address: string, method: TfaMethod) => {
    try {
      setSendIsPending(true);
      setSendError("");

      const response = await AccountMpProvider.changePasswordSendCode({
        twoFactorVerificationType: method === TfaMethod.AUTHENTICATOR ? "google" : method,
        payload: method === TfaMethod.AUTHENTICATOR ? undefined : address,
      });

      if (response instanceof RpcMpProviderSuccessResponse) {
        setSendIsCompleted(true);
      } else {
        setSendError(tError(response.message));
        setSendIsCompleted(false);
      }
    } catch ({ message }) {
      setSendError(tError(message));
      setSendIsCompleted(false);
    } finally {
      setSendIsPending(false);
    }
  }, []);

  const checkCodeHandler = useCallback(async (code: string) => {
    try {
      setCheckIsPending(true);
      setCheckError("");

      const response = await AccountMpProvider.changePasswordCheckCode({ verificationCode: code });

      if (response instanceof RpcMpProviderSuccessResponse) {
        setCheckIsCompleted(true);
      } else {
        setCheckError(tError(response.message));
        setCheckIsCompleted(false);
      }
    } catch ({ message }) {
      setCheckError(tError(message));
      setCheckIsCompleted(false);
    } finally {
      setCheckIsPending(false);
    }
  }, []);

  const setNewPasswordHandler = useCallback(async (password: string) => {
    try {
      setNewPasswordIsPending(true);
      setNewPasswordError("");

      const response = await AccountMpProvider.changePassword({
        password,
      });

      if (response instanceof RpcMpProviderSuccessResponse) {
        setNewPasswordIsCompleted(true);
      } else {
        setNewPasswordError(tError(response.message));
        setNewPasswordIsCompleted(false);
      }
    } catch ({ message }) {
      setNewPasswordError(tError(message));
      setNewPasswordIsCompleted(false);
    } finally {
      setNewPasswordIsPending(false);
    }
  }, []);

  return {
    sendCodeHandler,
    sendIsPending,
    sendError,
    sendIsCompleted,
    setSendIsCompleted,

    setNewPasswordHandler,
    newPasswordIsPending,
    newPasswordError,
    newPasswordIsCompleted,

    checkCodeHandler,
    checkError,
    checkIsPending,
    setCheckIsPending,
    checkIsCompleted,
  };
}
